import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link2, Image as ImageIcon, Search, Upload, ArrowRight, X, CircleCheck, AlertTriangle, ShieldAlert } from 'lucide-react';
import CloneScanningView from './CloneScanningView';

const INTENT_OPTIONS = [
  {
    id: 'match_visual_style', label: 'Match visual style', defaultFidelity: 72,
    desc: 'Keep the look and feel; rewrite content and CTAs for your brand.',
    bestFor: 'You love the reference design and want yours to feel similar.',
    preserves: 'Palette, typography, spacing, component feel',
    transforms: 'All copy, CTAs, proof, brand voice',
  },
  {
    id: 'structure_inspiration', label: 'Use structure', defaultFidelity: 50,
    desc: 'Borrow the layout flow and section order; use your own branding.',
    bestFor: 'The page structure is great, but you want a different visual identity.',
    preserves: 'Section sequence, hierarchy, conversion flow',
    transforms: 'Colors, fonts, imagery, all copy',
  },
  {
    id: 'redesign_reference', label: 'Redesign this site', defaultFidelity: 35,
    desc: 'Keep the business purpose; modernize the UI and conversion flow.',
    bestFor: 'The reference is outdated or has UX problems you want fixed.',
    preserves: 'Core IA and page purpose',
    transforms: 'Visuals, trust framing, CTA hierarchy, accessibility',
  },
  {
    id: 'create_my_version', label: 'Create my version', defaultFidelity: 20,
    desc: 'Use the reference as loose inspiration only; build something original.',
    bestFor: 'You want a fresh site that captures a similar vibe.',
    preserves: 'High-level mood and a few reusable cues',
    transforms: 'Everything — brand, layout, sections, copy',
  },
];

const FIDELITY_LABELS = [
  { max: 20, label: 'Loose inspiration', desc: 'Reference used as distant mood only' },
  { max: 40, label: 'Low fidelity', desc: 'General color mood and typography vibe borrowed' },
  { max: 60, label: 'Balanced', desc: 'Palette, fonts, and visual density matched' },
  { max: 80, label: 'High match', desc: 'Palette, typography, spacing, and section rhythm preserved' },
  { max: 100, label: 'Near-match', desc: 'Exact visual feel — spacing, proportions, and scale reproduced' },
];

function getFidelityLabel(value) {
  for (const band of FIDELITY_LABELS) {
    if (value <= band.max) return band;
  }
  return FIDELITY_LABELS[FIDELITY_LABELS.length - 1];
}

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CloneReferenceStage({ projectId, initialUrl = '', initialAnalysis = null, onComplete, onAnalyze, analysisFromStream = null }) {
  const [mode, setMode] = useState('url');
  const [refUrl, setRefUrl] = useState(initialUrl);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isDone, setIsDone] = useState(!!initialAnalysis);
  const [analysis, setAnalysis] = useState(initialAnalysis);
  const [screenshots, setScreenshots] = useState([]);
  const [cloneIntent, setCloneIntent] = useState('match_visual_style');
  const [referenceFidelity, setReferenceFidelity] = useState(72);
  const fileInputRef = useRef(null);

  // Sync analysis from SSE stream (parent drives the stream, feeds result here)
  useEffect(() => {
    if (analysisFromStream && !analysis) {
      setAnalysis(analysisFromStream);
      setIsDone(true);
      setIsAnalyzing(false);
      if (analysisFromStream.recommended_intent) {
        setCloneIntent(analysisFromStream.recommended_intent);
        const match = INTENT_OPTIONS.find(o => o.id === analysisFromStream.recommended_intent);
        if (match) setReferenceFidelity(match.defaultFidelity);
      }
    }
  }, [analysisFromStream]);

  const handleAnalyze = async () => {
    if (!refUrl.trim()) { toast.error('Please enter a URL'); return; }
    setIsAnalyzing(true);
    setIsDone(false);
    setAnalysis(null);

    // If parent provides SSE handler, use that (live progress in agent panel)
    if (onAnalyze) {
      onAnalyze(refUrl.trim());
      return; // parent will feed result back via analysisFromStream prop
    }

    // Fallback: direct POST (no streaming progress)
    try {
      const { data } = await axios.post(`${API_URL}/clone/analyze`, { project_id: projectId, url: refUrl.trim() }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('orbis_token')}` },
      });
      setAnalysis(data);
      setIsDone(true);
      if (data.recommended_intent) {
        setCloneIntent(data.recommended_intent);
        const match = INTENT_OPTIONS.find(o => o.id === data.recommended_intent);
        if (match) setReferenceFidelity(match.defaultFidelity);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analysis failed');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleScreenshotDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    setScreenshots(prev => [...prev, ...files].slice(0, 6));
  };

  const handleScreenshotPick = (e) => {
    const files = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
    setScreenshots(prev => [...prev, ...files].slice(0, 6));
  };

  const removeScreenshot = (i) => setScreenshots(prev => prev.filter((_, idx) => idx !== i));

  const canContinue = mode === 'url'
    ? (isDone && analysis)
    : (screenshots.length > 0 && !isAnalyzing);

  const handleContinue = async () => {
    if (mode === 'url') {
      onComplete(analysis, refUrl, { cloneIntent, referenceFidelity });
      return;
    }

    // Screenshot mode: upload files → analyze via Mode B → forward result
    if (!screenshots.length) return;
    setIsAnalyzing(true);
    setIsDone(false);
    setAnalysis(null);

    try {
      // Step 1: upload files
      const form = new FormData();
      screenshots.forEach(file => form.append('files', file));
      const { data: uploadData } = await axios.post(
        `${API_URL}/clone/projects/${projectId}/reference-screenshots`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('orbis_token')}`,
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      if (!uploadData?.count) {
        toast.error('Upload failed — no screenshots stored');
        setIsAnalyzing(false);
        return;
      }

      // Step 2: analyze (empty URL triggers Mode B)
      const { data: analysisData } = await axios.post(
        `${API_URL}/clone/analyze`,
        { project_id: projectId, url: '' },
        { headers: { Authorization: `Bearer ${localStorage.getItem('orbis_token')}` } },
      );

      setAnalysis(analysisData);
      setIsDone(true);

      // Pre-select recommended intent, same as URL flow
      let nextIntent = cloneIntent;
      let nextFidelity = referenceFidelity;
      if (analysisData.recommended_intent) {
        nextIntent = analysisData.recommended_intent;
        const match = INTENT_OPTIONS.find(o => o.id === analysisData.recommended_intent);
        if (match) nextFidelity = match.defaultFidelity;
        setCloneIntent(nextIntent);
        setReferenceFidelity(nextFidelity);
      }

      // Step 3: advance to the brief stage with the analysis result
      onComplete(analysisData, null, { cloneIntent: nextIntent, referenceFidelity: nextFidelity });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Screenshot analysis failed');
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="flex-1 flex min-h-0">
      {/* Left config panel */}
      <div
        className="flex flex-col gap-6 flex-shrink-0 overflow-auto"
        style={{ width: '360px', background: '#1A1A1A', borderRight: '0.5px solid rgba(255,255,255,0.08)', padding: '28px 24px' }}
      >
        {/* Mode toggle */}
        <div className="flex items-center gap-1 p-1" style={{ background: '#111111', borderRadius: '10px' }}>
          {[{ id: 'url', label: 'URL', icon: Link2 }, { id: 'screenshot', label: 'Screenshots', icon: ImageIcon }].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setMode(id)}
              className="flex items-center justify-center gap-2 flex-1 transition-all"
              style={{
                height: '34px', borderRadius: '8px', fontSize: '13px', fontWeight: 300,
                background: mode === id ? '#2A2A2A' : 'transparent',
                color: mode === id ? 'var(--orbis-warm)' : '#5A5855',
                border: mode === id ? '0.5px solid rgba(255,255,255,0.08)' : 'none',
              }}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        {mode === 'url' ? (
          <>
            {/* URL input */}
            <div className="flex flex-col gap-2">
              <label style={{ fontSize: '11px', fontWeight: 400, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#5A5855' }}>
                Reference URL
              </label>
              <div
                className="flex items-center gap-2 px-3"
                style={{ background: '#2A2A2A', border: '0.5px solid rgba(255,255,255,0.08)', borderRadius: '6px', height: '44px' }}
              >
                <Link2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--orbis-blue)' }} />
                <input
                  value={refUrl}
                  onChange={e => { setRefUrl(e.target.value); setIsDone(false); setAnalysis(null); }}
                  onKeyDown={e => e.key === 'Enter' && handleAnalyze()}
                  placeholder="stripe.com"
                  className="flex-1 bg-transparent border-none outline-none"
                  style={{ fontSize: '14px', fontWeight: 300, color: 'var(--orbis-warm)', fontFamily: 'var(--orbis-font)' }}
                />
              </div>
            </div>

            {/* Analyze button */}
            <button
              onClick={handleAnalyze}
              disabled={isAnalyzing || !refUrl.trim()}
              className="flex items-center justify-center gap-2 w-full transition-opacity"
              style={{
                height: '44px', borderRadius: '100px', background: 'var(--orbis-blue)', border: 'none',
                fontSize: '13px', fontWeight: 400, color: '#1A1A1A',
                cursor: (isAnalyzing || !refUrl.trim()) ? 'not-allowed' : 'pointer',
                opacity: (isAnalyzing || !refUrl.trim()) ? 0.5 : 1,
              }}
            >
              <Search className="w-3.5 h-3.5" />
              {isAnalyzing ? 'Analyzing...' : 'Analyze Website'}
            </button>

            {/* Analysis results */}
            {analysis && isDone && (
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <CircleCheck className="w-3.5 h-3.5" style={{ color: 'var(--orbis-green)' }} />
                  <span style={{ fontSize: '13px', color: 'var(--orbis-green)' }}>Analysis complete</span>
                </div>

                {/* Colors */}
                {Object.keys(analysis.colors || {}).length > 0 && (
                  <div className="flex flex-col gap-2">
                    <span style={{ fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5A5855' }}>Colors</span>
                    <div className="flex items-center gap-2 flex-wrap">
                      {Object.entries(analysis.colors).map(([key, val]) => (
                        <div key={key} title={`${key}: ${val}`} className="flex flex-col items-center gap-1">
                          <div className="w-7 h-7 rounded-full flex-shrink-0" style={{ background: val, border: '0.5px solid rgba(255,255,255,0.1)' }} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Fonts */}
                {analysis.fonts?.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <span style={{ fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5A5855' }}>Fonts</span>
                    <div className="flex items-center gap-2 flex-wrap">
                      {analysis.fonts.map(f => (
                        <span key={f} style={{ fontSize: '12px', fontWeight: 300, color: '#8A8882', padding: '3px 10px', borderRadius: '100px', background: 'rgba(255,255,255,0.06)' }}>{f}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sections */}
                {analysis.layout_sections?.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <span style={{ fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5A5855' }}>Sections</span>
                    <div className="flex items-center gap-2 flex-wrap">
                      {analysis.layout_sections.slice(0, 6).map(s => (
                        <span key={s} style={{ fontSize: '12px', fontWeight: 300, color: '#8A8882', padding: '3px 10px', borderRadius: '100px', background: 'rgba(255,255,255,0.06)', textTransform: 'capitalize' }}>{s}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Vibe */}
                {analysis.vibe && (
                  <p style={{ fontSize: '13px', fontWeight: 300, color: '#8A8882', fontStyle: 'italic' }}>
                    "{analysis.vibe}"
                  </p>
                )}

                {/* Reference Status Banner */}
                {analysis.analysis_status && analysis.analysis_status !== 'complete' && (
                  <div
                    className="flex items-center gap-2 px-3 py-2"
                    style={{
                      borderRadius: '8px',
                      background: analysis.analysis_status === 'blocked' ? 'rgba(239,68,68,0.08)' : 'rgba(234,179,8,0.08)',
                      border: `0.5px solid ${analysis.analysis_status === 'blocked' ? 'rgba(239,68,68,0.2)' : 'rgba(234,179,8,0.2)'}`,
                    }}
                  >
                    {analysis.analysis_status === 'blocked'
                      ? <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#ef4444' }} />
                      : <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#eab308' }} />
                    }
                    <span style={{ fontSize: '12px', fontWeight: 300, color: analysis.analysis_status === 'blocked' ? '#fca5a5' : '#fde68a' }}>
                      {analysis.analysis_status === 'blocked' ? 'Blocked reference — using style inspiration only' : 'Partial analysis — some details unavailable'}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* What we detected summary */}
            {analysis && isDone && (
              <div style={{ borderTop: '0.5px solid rgba(255,255,255,0.06)', paddingTop: '12px' }}>
                <p style={{ fontSize: '11px', color: '#5A5855', lineHeight: 1.5 }}>
                  We found: {analysis.fonts?.length || 0} font{(analysis.fonts?.length || 0) !== 1 ? 's' : ''}, {Object.keys(analysis.colors || {}).length} colors, {(analysis.layout_sections || []).slice(0, 4).join('/')}{(analysis.layout_sections || []).length > 4 ? ` +${analysis.layout_sections.length - 4}` : ''}.
                </p>
              </div>
            )}

            {/* Reference Strategy — explainer cards */}
            {analysis && isDone && (
              <div className="flex flex-col gap-3" style={{ borderTop: '0.5px solid rgba(255,255,255,0.06)', paddingTop: '16px' }}>
                <span style={{ fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5A5855' }}>Clone strategy</span>

                {INTENT_OPTIONS.map((opt) => {
                  const isSelected = cloneIntent === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => { setCloneIntent(opt.id); setReferenceFidelity(opt.defaultFidelity); }}
                      className="w-full text-left transition-all"
                      style={{
                        padding: '12px 14px', borderRadius: '10px', cursor: 'pointer',
                        background: isSelected ? 'rgba(184,204,223,0.06)' : '#111111',
                        border: isSelected ? '1px solid rgba(184,204,223,0.2)' : '0.5px solid rgba(255,255,255,0.06)',
                      }}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div style={{
                          width: '14px', height: '14px', borderRadius: '50%',
                          border: isSelected ? '4px solid var(--orbis-blue)' : '1.5px solid #5A5855',
                          background: isSelected ? 'var(--orbis-bg)' : 'transparent',
                          flexShrink: 0,
                        }} />
                        <span style={{ fontSize: '13px', fontWeight: isSelected ? 500 : 400, color: isSelected ? 'var(--orbis-warm)' : '#8A8882' }}>
                          {opt.label}
                        </span>
                      </div>
                      <p style={{ fontSize: '11px', fontWeight: 300, color: '#5A5855', lineHeight: 1.4, marginLeft: '22px' }}>
                        {opt.desc}
                      </p>
                      {isSelected && (
                        <div style={{ marginLeft: '22px', marginTop: '8px', fontSize: '10px', color: '#5A5855', lineHeight: 1.5 }}>
                          <span style={{ color: 'var(--orbis-green)' }}>Preserve:</span> {opt.preserves}<br/>
                          <span style={{ color: 'var(--orbis-blue)' }}>Transform:</span> {opt.transforms}
                        </div>
                      )}
                    </button>
                  );
                })}

                {/* Fidelity slider with live label */}
                <div className="flex flex-col gap-2 mt-1">
                  <div className="flex items-center justify-between">
                    <span style={{ fontSize: '11px', fontWeight: 400, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#5A5855' }}>Fidelity</span>
                    <span style={{ fontSize: '12px', fontWeight: 400, color: 'var(--orbis-blue)' }}>{getFidelityLabel(referenceFidelity).label}</span>
                  </div>
                  <input
                    type="range" min="0" max="100"
                    value={referenceFidelity}
                    onChange={e => setReferenceFidelity(Number(e.target.value))}
                    className="w-full"
                    style={{ accentColor: 'var(--orbis-blue)' }}
                  />
                  <p style={{ fontSize: '10px', fontWeight: 300, color: '#5A5855', lineHeight: 1.4 }}>
                    {getFidelityLabel(referenceFidelity).desc}
                  </p>
                  {(analysis.analysis_status === 'partial' || analysis.analysis_status === 'blocked') && referenceFidelity > 60 && (
                    <p style={{ fontSize: '10px', color: '#FBBF24', lineHeight: 1.4 }}>
                      High fidelity may not be possible — some reference signals were unavailable.
                    </p>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Screenshot upload */}
            <div className="flex flex-col gap-2">
              <label style={{ fontSize: '11px', fontWeight: 400, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#5A5855' }}>
                Upload Screenshots
              </label>
              <div
                onDragOver={e => e.preventDefault()}
                onDrop={handleScreenshotDrop}
                onClick={() => fileInputRef.current?.click()}
                className="flex flex-col items-center justify-center gap-3 cursor-pointer transition-colors"
                style={{
                  border: '0.5px dashed rgba(255,255,255,0.15)', borderRadius: '10px', minHeight: '120px',
                  background: '#111111', padding: '20px',
                }}
              >
                <Upload className="w-6 h-6" style={{ color: '#5A5855' }} />
                <div className="text-center">
                  <p style={{ fontSize: '13px', fontWeight: 300, color: '#8A8882' }}>Drop screenshots here</p>
                  <p style={{ fontSize: '12px', fontWeight: 300, color: '#5A5855', marginTop: '2px' }}>or click to browse — up to 6 images</p>
                </div>
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleScreenshotPick} />
            </div>

            {/* Thumbnails */}
            {screenshots.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {screenshots.map((file, i) => (
                  <div key={i} className="relative group" style={{ aspectRatio: '4/3' }}>
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      className="w-full h-full object-cover"
                      style={{ borderRadius: '6px', border: '0.5px solid rgba(255,255,255,0.08)' }}
                    />
                    <button
                      onClick={e => { e.stopPropagation(); removeScreenshot(i); }}
                      className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'rgba(0,0,0,0.7)', border: 'none', cursor: 'pointer' }}
                    >
                      <X className="w-3 h-3" style={{ color: 'var(--orbis-warm)' }} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Continue button */}
        <button
          onClick={handleContinue}
          disabled={!canContinue}
          className="flex items-center justify-center gap-2 w-full transition-opacity"
          style={{
            height: '48px', borderRadius: '100px', background: 'var(--orbis-blue)', border: 'none',
            fontSize: '14px', fontWeight: 400, color: '#1A1A1A',
            cursor: !canContinue ? 'not-allowed' : 'pointer',
            opacity: !canContinue ? 0.4 : 1,
          }}
        >
          Continue to Brand
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Right preview panel */}
      <div className="flex-1 overflow-hidden">
        {isAnalyzing ? (
          <CloneScanningView url={mode === 'url' ? refUrl : `${screenshots.length} screenshot${screenshots.length > 1 ? 's' : ''}`} isDone={false} />
        ) : isDone && analysis ? (
          <CloneScanningView url={analysis.url || `${screenshots.length} screenshot${screenshots.length > 1 ? 's' : ''}`} isDone={true} />
        ) : mode === 'screenshot' && screenshots.length > 0 ? (
          <div className="flex-1 flex items-center justify-center h-full" style={{ background: '#111111' }}>
            <div className="text-center">
              <ImageIcon className="w-10 h-10 mx-auto mb-3" style={{ color: '#5A5855' }} />
              <p style={{ fontSize: '13px', fontWeight: 300, color: '#8A8882' }}>{screenshots.length} screenshot{screenshots.length > 1 ? 's' : ''} ready — click Continue to analyze</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full gap-4" style={{ background: '#111111' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '14px', background: '#161616', border: '0.5px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Link2 className="w-6 h-6" style={{ color: '#5A5855' }} />
            </div>
            <div className="text-center">
              <p style={{ fontSize: '14px', fontWeight: 300, color: '#8A8882' }}>Enter a URL to analyze</p>
              <p style={{ fontSize: '13px', fontWeight: 300, color: '#5A5855', marginTop: '4px' }}>or upload screenshots of the website you want to clone</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
